import { useState } from 'react';
import { Box, Container, Paper, Stepper, Step, StepLabel, Typography, Button, Stack, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CompanyInfoStep } from './CompanyInfoStep';
import { AdminInfoStep } from './AdminInfoStep';
import { ReviewStep } from './ReviewStep';

export interface SignupData {
  companyName: string;
  registeredName: string;
  registrationNumber: string;
  entityType: 'Organisation' | 'Individual';
  contactPhone: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  adminPassword: string;
  termsAccepted: boolean;
}

const steps = ['Company Info', 'Admin Account', 'Review & Confirm'];
const stepDescriptions = [
  'Provide your organisation details. This creates your company profile in Rosta.',
  'Set up the administrator account. This user will have full access to manage your organisation.',
  'Review your details before creating your account. You can go back to make changes.',
];

export function SignUpWizard() {
  const [searchParams] = useSearchParams();
  const presetEmail = searchParams.get('email') || '';

  const [activeStep, setActiveStep] = useState(0);
  const [signupData, setSignupData] = useState<SignupData>({
    companyName: '',
    registeredName: '',
    registrationNumber: '',
    entityType: 'Organisation',
    contactPhone: '',
    adminEmail: presetEmail,
    adminFirstName: '',
    adminLastName: '',
    adminPassword: '',
    termsAccepted: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSignup = async () => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${apiUrl}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Signup failed');
      }

      localStorage.setItem('rosta_signup_token', result.data.signupToken);
      navigate('/verify-email/' + result.data.signupToken);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred during signup';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const updateData = (partial: Partial<SignupData>) => {
    setSignupData((prev) => ({ ...prev, ...partial }));
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#050510', py: 6 }}>
      <Container maxWidth="md">
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.06)',
            backgroundColor: 'rgba(18, 18, 31, 0.8)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#f0f0f5', mb: 1 }}>
              Create Your Account
            </Typography>
            <Typography sx={{ color: '#a0a5b5' }}>
              Set up your organization in under 2 minutes
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': { color: '#a0a5b5', fontWeight: 500 },
                    '& .MuiStepLabel-label.Mui-active': { color: '#3d7aff' },
                    '& .MuiStepLabel-label.Mui-completed': { color: '#22c55e' },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            {stepDescriptions[activeStep]}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ minHeight: 300 }}>
            {activeStep === 0 && (
              <CompanyInfoStep data={signupData} onChange={updateData} />
            )}
            {activeStep === 1 && (
              <AdminInfoStep data={signupData} onChange={updateData} />
            )}
            {activeStep === 2 && (
              <ReviewStep data={signupData} onChange={updateData} />
            )}
          </Box>

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ color: '#a0a5b5', textTransform: 'none' }}
            >
              Back
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)' },
                }}
              >
                Continue →
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSignup}
                disabled={loading || !signupData.termsAccepted}
                sx={{
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': { background: 'linear-gradient(135deg, #16a34a, #22c55e)' },
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account →'}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
